import { type Clip, hasClipSlotContent } from "@/shared/models/Clip";
import type { WorkOrder } from "@/shared/models/WorkOrder";
import { getClip } from "@/shared/repositories/ClipRepository";
import { Divider, Grid2, Paper, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { SectionTitle, SPECS_PANEL_SX, SpecField } from "./ProductSpecsPanel";

export type ClipSpecs = {
	partNumber: string | null;
	customerPartNumber: string | null;
	size: string;
	gauge: string | number | null;
	widthTolerance: string | number | null;
	heightTolerance: string | number | null;
	materialGrade: string | null;
	testCheckCycle: string | number | null;
	holePattern: string | null;
	holeGage: string | number | null;
	holeSizes: string | null;
	totalHoles: number | null;
};

/**
 * Clip specs from whichever source the surface has: the clip just picked in a
 * selector, else the (already joined) columns of the work order row.
 */
export const useClipSpecs = ({
	selectedClip,
	workOrderRow,
}: {
	selectedClip: Clip | null;
	workOrderRow: WorkOrder | null;
}): ClipSpecs | null =>
	useMemo(() => {
		if (selectedClip)
			return {
				partNumber: selectedClip.ClipPartNumber,
				customerPartNumber: selectedClip.CustomerClipPartNumber,
				size: `${selectedClip.Width} x ${selectedClip.Height}`,
				gauge: selectedClip.Gauge,
				widthTolerance: selectedClip.WidthTolerance,
				heightTolerance: selectedClip.HeightTolerance,
				materialGrade: selectedClip.MaterialGradeName,
				testCheckCycle: selectedClip.TestCheckCycle,
				holePattern: selectedClip.HolePattern,
				holeGage: selectedClip.HoleGage,
				holeSizes: selectedClip.HoleBreakdown,
				totalHoles: selectedClip.HoleQty,
			};
		if (workOrderRow?.ClipId != null)
			return {
				partNumber: workOrderRow.ClipPartNumber,
				customerPartNumber: workOrderRow.CustomerClipPartNumber,
				size:
					workOrderRow.ClipWidth != null || workOrderRow.ClipHeight != null
						? `${workOrderRow.ClipWidth} x ${workOrderRow.ClipHeight}`
						: "",
				gauge: workOrderRow.ClipGauge,
				widthTolerance: workOrderRow.ClipWidthTolerance,
				heightTolerance: workOrderRow.ClipHeightTolerance,
				materialGrade: workOrderRow.ClipMaterialGrade,
				testCheckCycle: workOrderRow.ClipTestCheckCycle,
				holePattern: workOrderRow.HolePattern,
				holeGage: workOrderRow.HoleGage,
				holeSizes: workOrderRow.ClipHoleSizes,
				totalHoles: workOrderRow.ClipTotalHoles,
			};
		return null;
	}, [selectedClip, workOrderRow]);

type Props = {
	specs: ClipSpecs | null;
	/** drives the hole lookup; specs alone don't carry the slots */
	clipId: number | null;
	emptyMessage?: string;
};

export const ClipSpecsPanel = ({
	specs,
	clipId,
	emptyMessage = "Select a clip to view specifications",
}: Props) => {
	// Hole detail lives in ClipPunchSlots — only GetClip returns it
	const { data: clipDetail } = useQuery<Clip | null, Error>({
		queryKey: ["clip", clipId],
		queryFn: () => getClip(clipId as number),
		enabled: clipId != null,
	});
	const clipSlots = (clipDetail?.Slots ?? []).filter(hasClipSlotContent);

	const fields: Array<[string, string | number | null, number?]> = specs
		? [
				["Customer Clip Part #", specs.customerPartNumber],
				["Size", specs.size],
				["Gauge", specs.gauge],
				["Width Tolerance", specs.widthTolerance],
				["Height Tolerance", specs.heightTolerance],
				["Test Check Cycle", specs.testCheckCycle],
				["Material Grade", specs.materialGrade, 6],
				["Hole Sizes", specs.holeSizes, 6],
				["Hole Pattern", specs.holePattern],
				["Hole Gage", specs.holeGage],
				["Total Holes", specs.totalHoles],
			]
		: [];

	return (
		<Paper variant="outlined" sx={SPECS_PANEL_SX}>
			<SectionTitle mb={2}>Clip Specifications</SectionTitle>

			{specs ? (
				<>
					<Grid2 container spacing={1.5}>
						{fields.map(([label, value, columns]) => (
							<Grid2 key={label} size={{ xs: 6, sm: columns ?? 4 }}>
								<SpecField label={label} value={value} />
							</Grid2>
						))}
					</Grid2>

					{clipSlots.length > 0 && (
						<>
							<Divider sx={{ my: 2 }} />
							<SectionTitle mb={1}>Holes</SectionTitle>
							{clipSlots.map((slot) => (
								<Grid2
									container
									spacing={1.5}
									key={slot.ClipPunchSlotId ?? slot.SequenceOrder}
									sx={{ mb: 2 }}
								>
									<Grid2 size={4}>
										<SpecField label="Hole Dia." value={slot.HoleDia} />
									</Grid2>
									<Grid2 size={4}>
										<SpecField label="Tolerance" value={slot.Tolerance} />
									</Grid2>
									<Grid2 size={4}>
										<SpecField label="Qty" value={slot.Quantity} />
									</Grid2>
									<Grid2 size={6}>
										<SpecField
											label="Punch Part #"
											value={slot.PunchPartNumber}
										/>
									</Grid2>
									<Grid2 size={6}>
										<SpecField
											label="Button Part #"
											value={slot.ButtonPartNumber}
										/>
									</Grid2>
								</Grid2>
							))}
						</>
					)}
				</>
			) : (
				<Typography color="text.secondary" sx={{ py: 3 }} align="center">
					{emptyMessage}
				</Typography>
			)}
		</Paper>
	);
};
